import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { useIndietro } from "@/hooks/useIndietro";
import { cn } from "@/lib/utils";

/**
 * Il tasto «Indietro» delle pagine fuori dal menu (06/10/2026). Sa se una
 * pagina prima c'e': se c'e' dice «Indietro» e ci torna; se non c'e' (chiosco
 * appena riaperto, link da una mail, app partita da una notifica) dice dove
 * porta e ci porta: `madre`/`nomeMadre`. Alto 48 px, con la scritta: si preme
 * col dito sul chiosco del banco e coi guanti, e si capisce senza indovinare
 * cosa vuol dire una freccia.
 */
export function TastoIndietro({ madre, nomeMadre, className }: { madre: string; nomeMadre: string; className?: string }) {
  const { torna, puoTornare } = useIndietro(madre);
  return (
    <button
      type="button"
      onClick={torna}
      className={cn(
        "-ml-2 inline-flex min-h-12 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm text-muted-foreground transition-colors hover:bg-surface hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        className,
      )}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {puoTornare ? "Indietro" : nomeMadre}
    </button>
  );
}

/**
 * La barra del ritorno: la stessa degli strumenti (ToolLayout, «← Strumenti»
 * fermo in cima mentre si scorre, chiesto da Davide il 29/08) col tasto qui
 * sopra.
 *
 * `ferma`: per le pagine a tutta altezza che non scorrono (h-[100dvh]). Li' la
 * barra appiccicosa `topbar-safe` scenderebbe sopra il contenuto
 * (src/test/paginaSicura.test.ts): si usa una testata semplice, e la tacca la
 * tiene gia' page-safe della pagina.
 */
export function BarraRitorno({
  madre,
  nomeMadre,
  larghezza = "max-w-2xl",
  ferma = false,
  className,
  children,
}: {
  madre: string;
  nomeMadre: string;
  larghezza?: string;
  ferma?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn(ferma ? "shrink-0" : "topbar-safe", "border-b border-border/40 print:hidden", className)}>
      <div className={cn("mx-auto flex w-full items-center justify-between gap-3 px-4 md:px-6", larghezza)}>
        <TastoIndietro madre={madre} nomeMadre={nomeMadre} />
        {children}
      </div>
    </div>
  );
}
